// src/components/BacktestSummary.tsx
import { useAppStore } from '../state/store';
import type { TradeSummaryStats } from '../state/types';

const formatPct = (value: number | null) =>
  value != null ? `${(value * 100).toFixed(2)}%` : '—';

const formatValue = (value: number | null) =>
  value != null ? value.toFixed(4) : '—';

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-rule pb-1">
      <span className="text-ink/80">{label}</span>
      <span className="font-medium text-ink tabular-nums">{value}</span>
    </div>
  );
}

function ExpressionHoldSummaryBlock({ stats }: { stats: TradeSummaryStats }) {
  const numLosses = stats.num_trades - stats.num_wins;

  const allTrades: { label: string; value: string }[] = [
    { label: '# Trades', value: String(stats.num_trades) },
    { label: 'Win Rate', value: formatPct(stats.win_rate) },
    { label: 'Avg Return', value: formatPct(stats.avg_return) },
    { label: 'Median Return', value: formatPct(stats.median_return) },
    { label: 'Payoff Ratio', value: formatValue(stats.payoff_ratio) },
    { label: 'OAPF', value: formatValue(stats.oapf) },
    { label: 't-Statistic', value: formatValue(stats.t_statistic) },
    { label: 'Ave # Bars', value: stats.avg_bars != null ? stats.avg_bars.toFixed(2) : '—' },
    { label: 'Avg Drawdown', value: formatPct(stats.avg_drawdown) },
    { label: 'Max Drawdown', value: formatPct(stats.max_drawdown) },
    { label: 'Longest Drawdown', value: stats.longest_drawdown_bars != null ? String(stats.longest_drawdown_bars) : '—' },
    { label: 'Avg Run-up', value: formatPct(stats.avg_runup) },
  ];

  const winningTrades: { label: string; value: string }[] = [
    { label: '# Wins', value: String(stats.num_wins) },
    { label: 'Avg Win', value: formatPct(stats.avg_win) },
    { label: 'Ave Win # Bars', value: stats.avg_win_bars != null ? stats.avg_win_bars.toFixed(2) : '—' },
  ];

  const losingTrades: { label: string; value: string }[] = [
    { label: '# Losses', value: String(numLosses) },
    { label: 'Avg Loss', value: formatPct(stats.avg_loss) },
    { label: 'Max Loss', value: formatPct(stats.max_loss) },
    { label: 'Ave Loss # Bars', value: stats.avg_loss_bars != null ? stats.avg_loss_bars.toFixed(2) : '—' },
  ];

  const sectionClass = 'grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3';
  const headingClass = 'mt-4 first:mt-0 text-ink font-medium border-b border-rule pb-1 mb-2';

  return (
    <div className="mt-4">
      <h3 className={headingClass}>All Trades</h3>
      <div className={sectionClass}>
        {allTrades.map(({ label, value }) => (
          <StatRow key={label} label={label} value={value} />
        ))}
      </div>
      <h3 className={headingClass}>Winning Trades</h3>
      <div className={sectionClass}>
        {winningTrades.map(({ label, value }) => (
          <StatRow key={label} label={label} value={value} />
        ))}
      </div>
      <h3 className={headingClass}>Losing Trades</h3>
      <div className={sectionClass}>
        {losingTrades.map(({ label, value }) => (
          <StatRow key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  );
}

type BacktestSummarySummary = {
  constant_hold_trades?: { [bars: number]: TradeSummaryStats };
  exit_on_first_profit_trades?: TradeSummaryStats;
  expression_hold?: TradeSummaryStats;
};

const BacktestSummary = ({ summary: summaryProp }: { summary?: BacktestSummarySummary | null } = {}) => {
  const summaryFromStore = useAppStore((s) => s.backtestSummary);
  const summary = summaryProp ?? summaryFromStore;

  if (!summary) return null;

  if (summary.expression_hold) {
    return <ExpressionHoldSummaryBlock stats={summary.expression_hold} />;
  }

  const constantHold = summary.constant_hold_trades ?? {};
  const rows = Object.entries(constantHold)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([bars, stats]) => ({
      label: `Hold ${bars} bar${bars === '1' ? '' : 's'}`,
      stats,
    }));

  const exitOnFirst = summary.exit_on_first_profit_trades;
  if (exitOnFirst) {
    rows.push({
      label: `Exit on First Profit or Hold ${rows.length} bars`,
      stats: exitOnFirst,
    });
  }

  return (
    <div className="overflow-x-auto mt-4">
      <table className="min-w-full table-auto border-collapse text-sm">
        <thead>
          <tr className="bg-newsprint-dark text-left">
            <th className="p-2">Strategy</th>
            <th className="p-2 text-right"># Trades</th>
            <th className="p-2 text-right"># Wins</th>
            <th className="p-2 text-right">Win Rate</th>
            <th className="p-2 text-right">Avg Return</th>
            <th className="p-2 text-right">Median Return</th>
            <th className="p-2 text-right">Avg Win</th>
            <th className="p-2 text-right">Avg Loss</th>
            <th className="p-2 text-right">Payoff Ratio</th>
            <th className="p-2 text-right">Max Loss</th>
            <th className="p-2 text-right">OAPF</th>
            <th className="p-2 text-right">Avg Drawdown</th>
            <th className="p-2 text-right">Max Drawdown</th>
            <th className="p-2 text-right">Avg Run-up</th>
            <th className="p-2 text-right">t-Statistic</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, stats }) => (
            <tr key={label} className="border-t border-rule">
              <td className="p-2">{label}</td>
              <td className="p-2 text-right">{stats.num_trades}</td>
              <td className="p-2 text-right">{stats.num_wins}</td>
              <td className="p-2 text-right">{formatPct(stats.win_rate)}</td>
              <td className="p-2 text-right">{formatPct(stats.avg_return)}</td>
              <td className="p-2 text-right">{formatPct(stats.median_return)}</td>
              <td className="p-2 text-right">{formatPct(stats.avg_win)}</td>
              <td className="p-2 text-right">{formatPct(stats.avg_loss)}</td>
              <td className="p-2 text-right">{formatValue(stats.payoff_ratio)}</td>
              <td className="p-2 text-right">{formatPct(stats.max_loss)}</td>
              <td className="p-2 text-right">{formatValue(stats.oapf)}</td>
              <td className="p-2 text-right">{formatPct(stats.avg_drawdown)}</td>
              <td className="p-2 text-right">{formatPct(stats.max_drawdown)}</td>
              <td className="p-2 text-right">{formatPct(stats.avg_runup)}</td>
              <td className="p-2 text-right">{formatValue(stats.t_statistic)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BacktestSummary;